import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type BulkInviteCsvMutationVariables,
  type GetBulkInviteQuery,
  MultipartUploadError,
  queryUtils,
  useBulkInviteCsvMutation,
  useBulkInviteValidateMutation,
  useGetBulkInviteQuery,
} from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { BulkInviteEmailStatus, BulkInviteGrantError, type BulkInvitePreviewRow } from '@vizit/organisms';
import * as React from 'react';

/**
 * The batch-level grant applied to every user in the CSV — one role, one scope type, N scope
 * ids (sent as one form entry each; the server caps at 100 and dedupes) — mirroring the
 * single-invite dialog's picker. Derived from the generated request-body type rather than
 * hand-written: the grant is applied to the mutation variables via spread, which bypasses
 * excess-property checks, so only a derived type turns spec drift into a compile error.
 */
type BulkInviteGrant = {
  [K in 'role' | 'scope_type' | 'scope_ids']-?: NonNullable<BulkInviteCsvMutationVariables[K]>;
};

interface UseBulkAddUsersDialogProps {
  /** The organization to invite users into. */
  organizationId: string | null;
  /** The batch grant for the whole CSV, or null while the selection is missing or incomplete. */
  grant?: BulkInviteGrant | null;
  /**
   * Whether this organization requires a grant on invites (`requiresInviteGrants`). Only decides
   * whether to fail fast before uploading — the server enforces the same rule either way
   * (400 GRANTS_REQUIRED).
   */
  requiresGrants?: boolean;
  /**
   * Fired with grant-related error copy, or null when an attempt starts, so the caller can
   * render it on the role/scope picker. Grant errors are also thrown as `BulkInviteGrantError`,
   * which the dialog does not render while an access-fields slot is present.
   */
  onGrantError?: (message: string | null) => void;
}

interface UseBulkAddUsersDialogReturn {
  /** Seam 1: server-authoritative validation of the uploaded file → preview rows. */
  onValidateFile: (file: File) => Promise<BulkInvitePreviewRow[]>;
  /** Seam 2: dispatch the uploaded file, invalidate the list, and toast a summary. */
  onSubmit: (file: File) => Promise<void>;
  /** Whether a dispatch is in flight. */
  isSubmitting: boolean;
}

/** Fallback copy for unrecognized `error_code`s and network failures — the raw error is logged, never shown. */
const VALIDATE_ERROR_MESSAGE = 'We could not validate that file. Please check it and try again.';
const DISPATCH_ERROR_MESSAGE = 'We could not send those invites. Please try again in a few minutes.';

/** Server-enforced cap per upload; mirrored here for copy only (the server is authoritative). */
const MAX_EMAILS_PER_UPLOAD = 100;

/**
 * Friendly copy per server `error_code`. Anything not listed falls back to the generic message
 * above, so a new server code degrades to "check the file" rather than leaking raw text.
 * `TOO_MANY_EMAILS` is handled separately because its copy includes the offending count.
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  EMPTY_CSV: 'No emails found in this file.',
  EMPTY_FILE: 'That file is empty.',
  NO_VALID_EMAILS: 'No valid email addresses in this file.',
  FILE_TOO_LARGE: 'That file is too large (max 3MB).',
  INVALID_FILE_FORMAT: "That file isn't a CSV.",
  CSV_PARSE_ERROR: "We couldn't read that CSV.",
  RATE_LIMIT_EXCEEDED: 'Too many uploads — please wait a minute and try again.',
};

/**
 * Copy for an invite attempted without a complete role/scope selection. Exported so the
 * single-invite flow renders the identical message — one string, both flows.
 */
export const GRANT_INCOMPLETE_MESSAGE = 'Pick a role and at least one scope for this invite.';

/**
 * Copy per grant-related server `error_code`. These belong to the role/scope selection rather
 * than the file, so they surface on the picker (via `onGrantError`) and are thrown as
 * `BulkInviteGrantError`. GRANTS_REQUIRED / INCOMPLETE_GRANT_FIELDS reuse the incomplete-selection
 * copy: both mean "the server wanted a grant this upload did not carry".
 */
const GRANT_ERROR_CODE_MESSAGES: Record<string, string> = {
  GRANTS_REQUIRED: GRANT_INCOMPLETE_MESSAGE,
  INCOMPLETE_GRANT_FIELDS: GRANT_INCOMPLETE_MESSAGE,
  TOO_MANY_SCOPES: 'Too many scopes selected. The limit is 100 per upload.',
  ROLE_NOT_GRANTABLE: 'That role cannot be granted in this organization.',
  ROLE_GRANT_FORBIDDEN: 'You do not have permission to grant that role and scope.',
  PORTFOLIO_NOT_FOUND: 'One of the selected portfolios no longer exists. Refresh and try again.',
  WORKSPACE_NOT_FOUND: 'One of the selected workspaces no longer exists. Refresh and try again.',
  ORGANIZATION_NOT_FOUND: 'This organization could not be found. Refresh and try again.',
};

/**
 * `TOO_MANY_EMAILS` copy. The count is read out of the server `detail` ("… (found 101)") as a number
 * only — the server string itself is never rendered — and is dropped if the shape ever changes.
 */
function formatTooManyEmails(detail: string | null): string {
  const found = detail?.match(/\(found (\d+)\)/)?.[1];
  const lead = found ? `That file has ${found} emails.` : 'That file has too many emails.';

  return `${lead} The limit is ${MAX_EMAILS_PER_UPLOAD} per upload — please split it.`;
}

/**
 * Turn an upload failure into user-safe copy: specific when the server sent a known `error_code`,
 * the caller's generic fallback otherwise (unknown code, non-JSON body, or a network error).
 */
function toUserMessage(error: unknown, fallback: string): string {
  if (!(error instanceof MultipartUploadError) || !error.errorCode) {
    return fallback;
  }

  if (error.errorCode === 'TOO_MANY_EMAILS') {
    return formatTooManyEmails(error.detail);
  }

  return ERROR_CODE_MESSAGES[error.errorCode] ?? fallback;
}

const KNOWN_STATUSES = new Set<string>(Object.values(BulkInviteEmailStatus));

/** Map a server `BulkInviteValidateStatus` string to the preview enum, defensively. */
function toEmailStatus(status: string): BulkInviteEmailStatus {
  return KNOWN_STATUSES.has(status) ? (status as BulkInviteEmailStatus) : BulkInviteEmailStatus.InvalidFormat;
}

/**
 * How often to re-check a dispatched job, and how long to wait before giving up. Dispatch at the
 * 100-email cap is typically seconds; the timeout only bounds the pathological case.
 */
export const JOB_POLL_INTERVAL_MS = 1_500;
export const JOB_POLL_TIMEOUT_MS = 45_000;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Build the completion-toast copy from the finished job's real counters,
 * e.g. "24 invites sent, 2 skipped, 1 failed".
 */
export function formatBulkInviteSummary(input: { sent: number; skipped: number; errored?: number }): string {
  const { sent, skipped, errored = 0 } = input;
  const parts = [`${sent} ${sent === 1 ? 'invite' : 'invites'} sent`];

  if (skipped > 0) {
    parts.push(`${skipped} skipped`);
  }
  if (errored > 0) {
    parts.push(`${errored} failed`);
  }

  return parts.join(', ');
}

/**
 * Build the copy for the immediate 202 toast. The dispatch endpoint reports what it *queued*, so
 * this says "queued" — the honest "sent" number only exists once the job completes.
 */
export function formatBulkInviteQueued(input: { queued: number; skipped: number }): string {
  const { queued, skipped } = input;
  const queuedLabel = `${queued} ${queued === 1 ? 'invite' : 'invites'} queued`;
  return skipped > 0 ? `${queuedLabel}, ${skipped} skipped` : queuedLabel;
}

const logBulkInviteError = (error: unknown, organizationId: string | null, message = 'Bulk user invite failed.') => {
  Sentry.addBreadcrumb({
    message,
    level: 'error',
    data: { organization_id: organizationId },
  });

  Sentry.captureException(error, {
    level: 'error',
    tags: {
      feature: SENTRY_FEATURE_TAGS.Organization,
      stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Organization].bulkInvite,
    },
    extra: {
      organization_id: organizationId,
    },
  });
};

/**
 * Business logic for the bulk-invite dialog. Both Phase-A seams now call the generated
 * bulk-invite endpoints: `onValidateFile` → validate (preview), `onSubmit` → csv (dispatch).
 * The hook owns list invalidation, the summary toast, and Sentry reporting.
 */
function useBulkAddUsersDialog(props: UseBulkAddUsersDialogProps): UseBulkAddUsersDialogReturn {
  const { organizationId, grant = null, requiresGrants = false, onGrantError } = props;
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { mutateAsync: validateBulkInvite } = useBulkInviteValidateMutation();
  const { mutateAsync: dispatchBulkInvite, isPending: isSubmitting } = useBulkInviteCsvMutation();

  // The poll outlives the dialog, so it must stop if the page unmounts mid-job.
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Re-check the job until the worker finishes it. Resolves with the finished job, or `null` if we
   * gave up (timeout, unmount, or a failed poll) — the caller degrades gracefully either way.
   */
  const waitForJob = React.useCallback(
    async (organizationId: string, bulkInviteId: string): Promise<GetBulkInviteQuery | null> => {
      const variables = { organization_id: organizationId, bulk_invite_id: bulkInviteId };
      const deadline = Date.now() + JOB_POLL_TIMEOUT_MS;

      while (Date.now() < deadline) {
        await delay(JOB_POLL_INTERVAL_MS);

        if (!isMountedRef.current) {
          return null;
        }

        try {
          // Deliberately NOT `queryClient.fetchQuery`: that honours the app's 5-minute `staleTime`
          // (`router-provider.tsx`), so every poll after the first would be answered from cache and
          // the loop would spin without ever hitting the network again. A poll must always ask the
          // server, so it calls the wrapper's fetcher directly and bypasses the cache.
          const job = await useGetBulkInviteQuery.fetcher(variables)();

          if (job.status !== 'PROCESSING') {
            return job;
          }
        } catch (error) {
          // A failed poll tells us nothing about the invites themselves — report it and stop
          // rather than hammering an endpoint that is already failing.
          logBulkInviteError(error, organizationId);
          return null;
        }
      }

      return null;
    },
    [],
  );

  /**
   * Refresh what a finished (or just-queued) bulk invite can have changed: the org's user list
   * always, and the role assignments when the batch carried a grant — otherwise the new rows
   * render with an empty Role/Scope until a manual reload (found in dev5 QA).
   */
  const refreshInvitedUsers = React.useCallback(
    async (organizationId: string) => {
      // Independent caches — refetch in parallel.
      await Promise.all([
        queryUtils.invalidateUsersForOrganization(queryClient, organizationId),
        grant ? queryUtils.invalidateRoleAssignments(queryClient, organizationId) : undefined,
      ]);
    },
    [queryClient, grant],
  );

  /**
   * Background watcher started after the 202: refresh the list once the invites actually exist,
   * and replace the "queued" toast's numbers with the job's real ones.
   */
  const watchJob = React.useCallback(
    async (organizationId: string, bulkInviteId: string) => {
      const job = await waitForJob(organizationId, bulkInviteId);

      if (!isMountedRef.current) {
        return;
      }

      // Refresh either way: a timed-out or errored job can still have invited some users.
      await refreshInvitedUsers(organizationId);

      if (!job) {
        addToast({
          title: 'Invites still processing',
          description: 'This is taking longer than usual. Refresh the page in a moment to see them.',
          level: AlertLevel.Info,
        });
        return;
      }

      addToast({
        title: job.status === 'ERROR' ? 'Some invites could not be sent' : 'Invites sent',
        description: formatBulkInviteSummary({ sent: job.sent, skipped: job.skipped, errored: job.errored }),
        // Success, so it reads with the green check like the other completion toasts on this page
        // ("User removed", "Invite sent!") rather than the neutral info style.
        level: job.status === 'ERROR' ? AlertLevel.Error : AlertLevel.Success,
      });
    },
    [waitForJob, refreshInvitedUsers, addToast],
  );

  /**
   * Start-of-attempt gate for both seams: clear any stale picker error, then fail fast when the
   * organization requires a grant and the selection is incomplete — same rule and copy as the
   * single-invite flow. Skipping the upload here is UX only; the server enforces it regardless.
   */
  const ensureGrantComplete = React.useCallback(() => {
    onGrantError?.(null);
    if (requiresGrants && !grant) {
      onGrantError?.(GRANT_INCOMPLETE_MESSAGE);
      throw new BulkInviteGrantError(GRANT_INCOMPLETE_MESSAGE);
    }
  }, [onGrantError, requiresGrants, grant]);

  /**
   * Log the technical error and build the user-safe one to throw in its place: grant-related
   * codes go to the picker (as `BulkInviteGrantError`, which the dialog does not render itself),
   * everything else keeps the existing file-level mapping.
   */
  const toSeamError = React.useCallback(
    (error: unknown, fallback: string): Error => {
      logBulkInviteError(error, organizationId);

      if (error instanceof MultipartUploadError && error.errorCode) {
        const grantMessage = GRANT_ERROR_CODE_MESSAGES[error.errorCode];
        if (grantMessage) {
          onGrantError?.(grantMessage);
          return new BulkInviteGrantError(grantMessage);
        }
      }

      return new Error(toUserMessage(error, fallback));
    },
    [organizationId, onGrantError],
  );

  // ── Seam 1: validate (server-authoritative preview) ─────────────────────────
  const onValidateFile = React.useCallback(
    async (file: File): Promise<BulkInvitePreviewRow[]> => {
      if (!organizationId) {
        throw new Error(VALIDATE_ERROR_MESSAGE);
      }

      ensureGrantComplete();

      try {
        const response = await validateBulkInvite({ organization_id: organizationId, file, ...grant });
        return response.results.map((item) => ({ email: item.email, status: toEmailStatus(item.status) }));
      } catch (error) {
        // `multipartPost` throws with the raw body on `.message`. Log that, then re-throw copy
        // derived from the structured `error_code` so the reason is visible but never raw.
        throw toSeamError(error, VALIDATE_ERROR_MESSAGE);
      }
    },
    [organizationId, validateBulkInvite, grant, ensureGrantComplete, toSeamError],
  );

  // ── Seam 2: dispatch (csv endpoint) ─────────────────────────────────────────
  const onSubmit = React.useCallback(
    async (file: File) => {
      if (!organizationId) {
        throw new Error(DISPATCH_ERROR_MESSAGE);
      }

      ensureGrantComplete();

      Sentry.addBreadcrumb({
        message: 'Starting bulk user invite.',
        level: 'info',
        data: { organization_id: organizationId },
      });

      let response: Awaited<ReturnType<typeof dispatchBulkInvite>>;

      try {
        response = await dispatchBulkInvite({ organization_id: organizationId, file, ...grant });
      } catch (error) {
        // Log the technical error, then re-throw user-safe copy. The dialog surfaces this
        // inline (dialog stays open) — no separate error toast, so only one message shows.
        throw toSeamError(error, DISPATCH_ERROR_MESSAGE);
      }

      // Past the 202 the invites are queued server-side, so nothing below may reject: reporting a
      // refresh or toast failure as "we could not send those invites" makes the user upload again
      // and invite everyone twice.
      try {
        // The 202 only means "queued" — the worker creates the users afterwards. Invalidate now to
        // catch a job that finished instantly, then keep watching so the rows appear on their own.
        await refreshInvitedUsers(organizationId);

        addToast({
          title: 'Invites queued',
          description: formatBulkInviteQueued({ queued: response.sent, skipped: response.skipped }),
          level: AlertLevel.Info,
        });

        // Deliberately not awaited: the dialog closes on the 202 and the watcher runs behind it.
        void watchJob(organizationId, response.id);
      } catch (error) {
        logBulkInviteError(error, organizationId, 'Bulk user invite was queued, but the follow-up failed.');
      }
    },
    [
      organizationId,
      dispatchBulkInvite,
      grant,
      ensureGrantComplete,
      toSeamError,
      refreshInvitedUsers,
      addToast,
      watchJob,
    ],
  );

  return {
    onValidateFile,
    onSubmit,
    isSubmitting,
  };
}

export { useBulkAddUsersDialog };
export type { BulkInviteGrant, UseBulkAddUsersDialogProps, UseBulkAddUsersDialogReturn };
